import { AxiosError } from 'axios'
import { store } from '../../app/store'
import APIClient from '../../apiClient'
import { ErrorT } from '../../types/errorT'
import {
  NotificationT,
  GetNotificationsParams,
  NotificationsResponseT,
  UpdateNotificationStatusT,
} from '../../types/notificationT'
import {
  setError,
  setNotifications,
  setLoading,
  updateNotificationStatus,
  startUpdatingNotification,
  stopUpdatingNotification,
  setBulkReading,
  setBulkDismissing,
} from './slice'

const apiClient = new APIClient()
const apiInstance = apiClient.getInstance()

export const getUserNotifications =
  (params?: GetNotificationsParams) =>
  (dispatch: typeof store.dispatch) => {
    dispatch(setLoading(true))

    const queryParams = new URLSearchParams()
    if (params?.status) queryParams.append('status', params.status)
    if (params?.type) queryParams.append('type', params.type)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.offset) queryParams.append('offset', params.offset.toString())

    const url = `/notification${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out. Please check your internet connection and try again.')), 10000)
    )

    const apiCall = apiInstance.get<NotificationsResponseT>(url)

    Promise.race([apiCall, timeoutPromise])
      .then((response) => {
        if (response.data?.data) {
          dispatch(setNotifications({
            notifications: response.data.data,
            pagination: response.data.pagination,
          }))
        } else {
          dispatch(setNotifications({
            notifications: [],
            pagination: { total: 0, limit: 50, offset: 0 },
          }))
        }
      })
      .catch((error: ErrorT | AxiosError | Error) => {
        if (error instanceof AxiosError) {
          const { status, name, message } = error
          dispatch(setError({ status, name, message }))
        } else if (error instanceof Error) {
          dispatch(setError({
            status: 408,
            name: 'TimeoutError',
            message: error.message
          }))
        } else {
          dispatch(setError(error))
        }
      })
  }

export const updateNotificationStatusAction =
  (notificationId: string, statusUpdate: UpdateNotificationStatusT) =>
  (dispatch: typeof store.dispatch) => {
    dispatch(startUpdatingNotification(notificationId))

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out. Please check your internet connection and try again.')), 10000)
    )

    const apiCall = apiInstance.put<{ data: NotificationT }>(
      `/notification/${notificationId}`,
      statusUpdate,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    Promise.race([apiCall, timeoutPromise])
      .then((response) => {
        if (response.data?.data) {
          dispatch(updateNotificationStatus({
            notificationId,
            status: statusUpdate.status,
          }))
        }
      })
      .catch((error: ErrorT | AxiosError | Error) => {
        if (error instanceof AxiosError) {
          const { status, name, message } = error
          dispatch(setError({ status, name, message }))
        } else if (error instanceof Error) {
          dispatch(setError({
            status: 408,
            name: 'TimeoutError',
            message: error.message
          }))
        } else {
          dispatch(setError(error))
        }
      })
      .finally(() => {
        // Stop loading for this specific notification
        dispatch(stopUpdatingNotification(notificationId))
      })
  }

export const markNotificationAsRead =
  (notificationId: string) =>
  (dispatch: typeof store.dispatch) => {
    dispatch(updateNotificationStatusAction(notificationId, { status: 'read' }))
  }

export const dismissNotification =
  (notificationId: string) =>
  (dispatch: typeof store.dispatch) => {
    dispatch(updateNotificationStatusAction(notificationId, { status: 'dismissed' }))
  }

export const bulkReadNotifications =
  (notificationIds: string[]) =>
  (dispatch: typeof store.dispatch) => {
    dispatch(setBulkReading(true))

    const promises = notificationIds.map(id =>
      new Promise<void>((resolve) => {
        dispatch(updateNotificationStatusAction(id, { status: 'read' }))
        // Add a small delay to avoid overwhelming the API
        setTimeout(resolve, 100)
      })
    )

    Promise.all(promises).finally(() => {
      dispatch(setBulkReading(false))
    })
  }

export const bulkDismissNotifications =
  (notificationIds: string[]) =>
  (dispatch: typeof store.dispatch) => {
    dispatch(setBulkDismissing(true))

    const promises = notificationIds.map(id =>
      new Promise<void>((resolve) => {
        dispatch(updateNotificationStatusAction(id, { status: 'dismissed' }))
        // Add a small delay to avoid overwhelming the API
        setTimeout(resolve, 100)
      })
    )

    Promise.all(promises).finally(() => {
      dispatch(setBulkDismissing(false))
    })
  }
